import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { QrCodeDialog } from "../components/QrCodeDialog";
import { api, formatApiError } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Plus, Copy, ExternalLink, Trash2, Pencil, Search, QrCode } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { useLang } from "../context/LanguageContext";

// Config per category. All 4 lists use the same component with different labels.
const CATEGORY_CONFIG = {
  tour: {
    titleKey: "tours_title",
    subtitleKey: "tours_subtitle",
    newBtnKey: "new_tour_btn",
    emptyKey: "no_tours",
    emptyDescKey: "no_tours_desc",
    createKey: "create_tour",
  },
  restaurant: {
    titleKey: "restaurants_title",
    subtitleKey: "restaurants_subtitle",
    newBtnKey: "new_restaurant_btn",
    emptyKey: "no_restaurants",
    emptyDescKey: "no_restaurants_desc",
    createKey: "new_restaurant_btn",
  },
  hotel: {
    titleKey: "hotels_title",
    subtitleKey: "hotels_subtitle",
    newBtnKey: "new_hotel_btn",
    emptyKey: "no_hotels",
    emptyDescKey: "no_hotels_desc",
    createKey: "new_hotel_btn",
  },
  service: {
    titleKey: "services_title",
    subtitleKey: "services_subtitle",
    newBtnKey: "new_service_btn",
    emptyKey: "no_services",
    emptyDescKey: "no_services_desc",
    createKey: "new_service_btn",
  },
};

const CATEGORY_ROUTES = {
  tour: "/dashboard/tours",
  restaurant: "/dashboard/restaurants",
  hotel: "/dashboard/hotels",
  service: "/dashboard/services",
};

export default function ToursList({ category = "tour" }) {
  const navigate = useNavigate();
  const { t } = useLang();
  const cfg = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.tour;
  const routeBase = CATEGORY_ROUTES[category] || CATEGORY_ROUTES.tour;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tours", { params: { category } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [category]);

  const remove = async (id) => {
    try {
      await api.delete(`/tours/${id}`);
      toast.success(t("toast_tour_deleted"));
      setItems((s) => s.filter((tr) => tr.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyLink = (id) => {
    const link = `${window.location.origin}/r/${id}`;
    navigator.clipboard.writeText(link);
    toast.success(t("toast_link_copied"));
  };

  const filtered = items.filter((tr) => tr.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <DashboardLayout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#0A2540] tracking-tight" data-testid={`list-title-${category}`}>
            {t(cfg.titleKey)}
          </h1>
          <p className="text-slate-600 mt-1 text-sm">{t(cfg.subtitleKey)}</p>
        </div>
        <Button
          onClick={() => navigate(`${routeBase}/new`)}
          data-testid={`create-${category}-btn`}
          className="rounded-md bg-[#0A2540] hover:bg-[#0A2540]/90 text-white gap-2"
        >
          <Plus className="w-4 h-4" /> {t(cfg.newBtnKey)}
        </Button>
      </div>

      <div className="relative mb-6 max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder={t("search_ph")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-10" data-testid="tour-search-input" />
      </div>

      {loading ? (
        <div className="text-slate-500">{t("loading")}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
          <div className="font-display text-xl font-semibold text-[#0A2540]">{t(cfg.emptyKey)}</div>
          <p className="text-slate-500 mt-2 text-sm">{t(cfg.emptyDescKey)}</p>
          <Button
            onClick={() => navigate(`${routeBase}/new`)}
            className="mt-6 rounded-md bg-[#E57C4F] hover:bg-[#E57C4F]/90 text-white"
            data-testid={`empty-create-${category}-btn`}
          >
            <Plus className="w-4 h-4 mr-1" /> {t(cfg.createKey)}
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((tr) => {
            const link = `${window.location.origin}/r/${tr.id}`;
            const configured = ["tripadvisor", "google", "trustpilot", "facebook"].filter((k) => tr.platforms?.[k]).length;
            return (
              <div key={tr.id} className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col" data-testid={`tour-card-${tr.id}`}>
                <div className="font-display font-semibold text-lg text-[#0A2540] truncate">{tr.name}</div>
                <div className="text-xs text-slate-500 mt-1 line-clamp-2 min-h-[2.5rem]">{tr.description || t("no_description")}</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">{t("n_platforms", { n: configured })}</span>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-[#2B8C9F]/10 text-[#2B8C9F]">{tr.clicks} {t("events_opens")}</span>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-[#10B981]/10 text-[#10B981]">{tr.completions} {t("events_completed")}</span>
                </div>
                <div className="mt-4 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-xs text-slate-600 truncate" title={link}>
                  {link}
                </div>
                <div className="mt-4 flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => copyLink(tr.id)} data-testid={`copy-link-${tr.id}`}>
                    <Copy className="w-3.5 h-3.5 mr-1.5" /> {t("copy_link_btn")}
                  </Button>
                  <QrCodeDialog
                    url={link}
                    name={tr.name}
                    trigger={
                      <Button size="sm" variant="outline" data-testid={`qr-btn-${tr.id}`}>
                        <QrCode className="w-3.5 h-3.5 mr-1.5" /> {t("qr_code_btn")}
                      </Button>
                    }
                  />
                  <Button size="sm" variant="outline" asChild data-testid={`preview-tour-${tr.id}`}>
                    <a href={`/r/${tr.id}`} target="_blank" rel="noreferrer">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`${routeBase}/${tr.id}`)} data-testid={`edit-tour-${tr.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" data-testid={`delete-tour-${tr.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("del_confirm_title", { name: tr.name })}</AlertDialogTitle>
                        <AlertDialogDescription>{t("del_confirm_desc")}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-cancel">{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => remove(tr.id)} data-testid={`delete-confirm-${tr.id}`}>{t("delete")}</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
}
